import {
  Box,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { capitalCase } from "change-case";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ProposalActionsCell from "components/proposals/ProposalActionsCell";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import dayjs from "dayjs";
import {
  GetProposalsSortKey,
  Proposal,
  SortDirection,
  ProposalStatus,
  UserPermissions,
} from "generated/graphql";
import useProposalsTableData from "hooks/proposals/useProposalsTableData";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import { StyledTableRow } from "modules/jobs/JobsSchedules/JobsSchedules";
import ProposalExpandedRows from "modules/proposals/ProposalExpandedRows";
import React, { useCallback, useState, useEffect, useMemo } from "react";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { visuallyHidden } from "@mui/utils";
import { Link } from "react-router-dom";
import MultipleChoiceFilter from "components/filters/MultipleChoiceFilter/MultipleChoiceFilter";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import { getRoute } from "utils/routing";

export default function ListProposals() {
  const { hasPermissions } = useCheckPermissions();

  const createButton = useMemo(() => {
    if (!hasPermissions(UserPermissions.MANAGE_PROPOSALS)) return null;

    return (
      <MDTypography as={Link} to={getRoute("proposals.create")}>
        <MDButton color="blue">
          <Icon>add</Icon>&nbsp; Create Proposal
        </MDButton>
      </MDTypography>
    );
  }, [hasPermissions]);

  const {
    data: proposals,
    loading,
    error,
    pagination,
    sorting,
    filterControl,
    companiesData,
    companiesLoading,
    companiesError,
    companiesSearch,
    loadInitialCompanies,
    loadMoreCompanies,
    resetSearchCompanies,
    companiesHasMorePages,
    filtering,
    organizationUsersLoading,
    organizationUsersData,
    organizationUsersError,
  } = useProposalsTableData({ rightSlot: createButton });

  const [openStatus, setOpenStatus] = useState<Record<string, boolean | undefined>>({});
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Record<string, boolean | undefined>>({});
  const [hiddenProposalIds, setHiddenProposalIds] = useState<Record<string, boolean | undefined>>(
    {}
  );

  useEffect(() => {
    localStorage.setItem("ListProposalsTableFilters", JSON.stringify(filtering));
  }, [filtering]);

  const handleToggle = useCallback((id: Proposal["id"]) => {
    setOpenStatus((prevState) => ({
      ...prevState,
      [id]: typeof prevState[id] === "undefined" ? true : !prevState[id],
    }));
  }, []);

  const handleDeleteStart = useCallback((id: Proposal["id"]) => {
    setPendingDeleteIds((prevState) => ({ ...prevState, [id]: true }));
  }, []);

  const handleDeleteSuccess = useCallback((id: Proposal["id"]) => {
    setHiddenProposalIds((prevState) => ({ ...prevState, [id]: true }));
    setPendingDeleteIds((prevState) => ({ ...prevState, [id]: false }));
    setOpenStatus((prevState) => ({ ...prevState, [id]: false }));
  }, []);

  const handleDeleteSettled = useCallback((id: Proposal["id"]) => {
    setPendingDeleteIds((prevState) => ({ ...prevState, [id]: false }));
  }, []);

  const handleStatusChecked = useCallback(
    (selectedOptions) => {
      return filtering.setStatuses(selectedOptions);
    },
    [filtering]
  );

  const handleClientChecked = useCallback(
    (selectedOptions) => {
      return filtering.setMultipleCompanyIds(selectedOptions);
    },
    [filtering]
  );

  const handleEstimatorChecked = useCallback(
    (selectedOptions) => {
      return filtering.setMultipleUserIDs(selectedOptions);
    },
    [filtering]
  );

  const renderStageNames = (stages: Array<{ name?: string }>) => {
    if (!stages.length) {
      return null;
    }

    const [firstStage, ...otherStages] = stages;

    let stageNames = firstStage.name;
    if (otherStages.length) {
      stageNames += ` + ${otherStages.length}`;
    }

    return stageNames;
  };

  const getLastActivity = (proposal: any) => {
    const lastEvent = proposal.lastEvent;
    const lastStatusChange = proposal.lastStatusChange;

    if (!lastEvent && !lastStatusChange) {
      return null;
    }

    if (!lastEvent) return { type: "status" as const, data: lastStatusChange };
    if (!lastStatusChange) return { type: "event" as const, data: lastEvent };

    const eventTime = new Date(lastEvent.createdAt).getTime();
    const statusTime = new Date(lastStatusChange.changeAt).getTime();

    return eventTime >= statusTime
      ? { type: "event" as const, data: lastEvent }
      : { type: "status" as const, data: lastStatusChange };
  };

  const getProposalCompanyLabel = (proposal: any) => {
    if (proposal.proposalRole === "PACKAGE_PARENT") {
      return "Multiple Companies";
    }

    return proposal.company?.name ?? "-";
  };

  const getProposalStatusLabel = (proposal: any) => {
    const run = proposal.latestPackageSendRun;
    if (
      proposal.proposalRole === "PACKAGE_PARENT" &&
      ["QUEUED", "PROCESSING"].includes(run?.status)
    ) {
      return `Sending ${run.processedRecipients} of ${run.totalRecipients}`;
    }

    return capitalCase(proposal.status);
  };

  return (
    <>
      <MDBox mb={3}>{filterControl}</MDBox>
      {(loading || error) && (
        <div style={{ padding: "16px" }}>
          {loading && <TableSkeleton numberOfLines={pagination.pageSize} />}
          {error && <MDAlert color="error">{error.message}</MDAlert>}
        </div>
      )}
      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow sx={{ backgroundColor: "grey.300" }}>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetProposalsSortKey.ID
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <TableSortLabel
                    active={sorting.sortKey === GetProposalsSortKey.ID}
                    direction={
                      sorting.sortKey === GetProposalsSortKey.ID
                        ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => {
                      const isAsc =
                        sorting.sortKey === GetProposalsSortKey.ID &&
                        sorting.sortDirection === SortDirection.ASC;
                      sorting.setSort({
                        sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                        sortKey: GetProposalsSortKey.ID,
                      });
                    }}
                  >
                    <MDBox display="flex" alignItems="center">
                      ID <Icon>sort</Icon>
                    </MDBox>
                    {sorting.sortKey === GetProposalsSortKey.ID ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sorting.sortDirection === SortDirection.DESC
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetProposalsSortKey.COMPANY
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <MDBox display={"flex"}>
                    <TableSortLabel
                      active={sorting.sortKey === GetProposalsSortKey.COMPANY}
                      direction={
                        sorting.sortKey === GetProposalsSortKey.COMPANY
                          ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                          : "asc"
                      }
                      onClick={() => {
                        const isAsc =
                          sorting.sortKey === GetProposalsSortKey.COMPANY &&
                          sorting.sortDirection === SortDirection.ASC;
                        sorting.setSort({
                          sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                          sortKey: GetProposalsSortKey.COMPANY,
                        });
                      }}
                    >
                      <MDBox display="flex" alignItems="center">
                        Client Name <Icon>sort</Icon>
                      </MDBox>
                      {sorting.sortKey === GetProposalsSortKey.COMPANY ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sorting.sortDirection === SortDirection.DESC
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>

                    <MultipleChoiceFilter
                      id={"company.name"}
                      value={filtering.multipleCompanyIDs}
                      loading={companiesLoading}
                      error={companiesError}
                      options={
                        companiesData?.map((company) => ({
                          label: company.name,
                          value: company.id,
                        })) ?? []
                      }
                      search={companiesSearch}
                      onOpen={loadInitialCompanies}
                      onClose={resetSearchCompanies}
                      onEndReached={loadMoreCompanies}
                      hasMorePages={companiesHasMorePages}
                      onChange={handleClientChecked}
                    />
                  </MDBox>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetProposalsSortKey.STAGE
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <TableSortLabel
                    active={sorting.sortKey === GetProposalsSortKey.STAGE}
                    direction={
                      sorting.sortKey === GetProposalsSortKey.STAGE
                        ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => {
                      const isAsc =
                        sorting.sortKey === GetProposalsSortKey.STAGE &&
                        sorting.sortDirection === SortDirection.ASC;
                      sorting.setSort({
                        sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                        sortKey: GetProposalsSortKey.STAGE,
                      });
                    }}
                  >
                    <MDBox display="flex" alignItems="center">
                      Job Name <Icon>sort</Icon>
                    </MDBox>
                    {sorting.sortKey === GetProposalsSortKey.STAGE ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sorting.sortDirection === SortDirection.DESC
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetProposalsSortKey.STATUS
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <MDBox display={"flex"}>
                    <TableSortLabel
                      active={sorting.sortKey === GetProposalsSortKey.STATUS}
                      direction={
                        sorting.sortKey === GetProposalsSortKey.STATUS
                          ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                          : "asc"
                      }
                      onClick={() => {
                        const isAsc =
                          sorting.sortKey === GetProposalsSortKey.STATUS &&
                          sorting.sortDirection === SortDirection.ASC;
                        sorting.setSort({
                          sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                          sortKey: GetProposalsSortKey.STATUS,
                        });
                      }}
                    >
                      <MDBox display="flex" alignItems="center">
                        Status <Icon>sort</Icon>
                      </MDBox>
                      {sorting.sortKey === GetProposalsSortKey.STATUS ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sorting.sortDirection === SortDirection.DESC
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>

                    <MultipleChoiceFilter
                      id={"status"}
                      value={filtering.statuses}
                      options={enumToValueOptions(ProposalStatus)}
                      onChange={handleStatusChecked}
                    />
                  </MDBox>
                </TableCell>
                <TableCell>
                  <MDBox display="flex" alignItems="center">
                    Estimator
                    <MultipleChoiceFilter
                      id={"estimator"}
                      value={filtering.multipleUserIDs}
                      loading={organizationUsersLoading}
                      error={organizationUsersError}
                      options={
                        organizationUsersData?.map((user) => ({
                          label: user.name,
                          value: user.id,
                        })) ?? []
                      }
                      onChange={handleEstimatorChecked}
                    />
                  </MDBox>
                </TableCell>
                <TableCell align="left">
                  <MDBox display="flex" alignItems="center">
                    Contact Name
                  </MDBox>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetProposalsSortKey.CREATED
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <TableSortLabel
                    active={sorting.sortKey === GetProposalsSortKey.CREATED}
                    direction={
                      sorting.sortKey === GetProposalsSortKey.CREATED
                        ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => {
                      const isAsc =
                        sorting.sortKey === GetProposalsSortKey.CREATED &&
                        sorting.sortDirection === SortDirection.ASC;
                      sorting.setSort({
                        sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                        sortKey: GetProposalsSortKey.CREATED,
                      });
                    }}
                  >
                    <MDBox display="flex" alignItems="center">
                      Created Date <Icon>sort</Icon>
                    </MDBox>
                    {sorting.sortKey === GetProposalsSortKey.CREATED ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sorting.sortDirection === SortDirection.DESC
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetProposalsSortKey.SENT
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <TableSortLabel
                    active={sorting.sortKey === GetProposalsSortKey.SENT}
                    direction={
                      sorting.sortKey === GetProposalsSortKey.SENT
                        ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => {
                      const isAsc =
                        sorting.sortKey === GetProposalsSortKey.SENT &&
                        sorting.sortDirection === SortDirection.ASC;
                      sorting.setSort({
                        sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                        sortKey: GetProposalsSortKey.SENT,
                      });
                    }}
                  >
                    <MDBox display="flex" alignItems="center">
                      Sent Date <Icon>sort</Icon>
                    </MDBox>
                    {sorting.sortKey === GetProposalsSortKey.SENT ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sorting.sortDirection === SortDirection.DESC
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left">Proposal Total</TableCell>
                <TableCell align="left">Last Event</TableCell>
                <TableCell align="left">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals
                ?.filter((proposal) => !hiddenProposalIds[proposal.id])
                .map((proposal, i) => {
                  const isDeletePending = !!pendingDeleteIds[proposal.id];

                  return (
                    <React.Fragment key={proposal.id}>
                      <StyledTableRow
                        className={i % 2 === 0 ? "outer even" : "outer odd"}
                        key={proposal.id}
                        sx={{ opacity: isDeletePending ? 0.5 : 1 }}
                      >
                        <TableCell sx={{ position: "relative", minWidth: "100px" }}>
                          <MDBox display="flex">
                            <IconButton
                              sx={{ p: 0 }}
                              disabled={isDeletePending}
                              onClick={() => handleToggle(proposal.id)}
                            >
                              <Icon
                                className={`chevron ${
                                  openStatus[proposal.id] ? "expand_more" : "expand_less"
                                }`}
                              >
                                expand_less
                              </Icon>
                            </IconButton>
                            <MDBox>
                              <Link
                                style={{ color: "inherit" }}
                                to={`/proposals/${proposal.id}/info`}
                              >
                                {proposal.externalIdWithPrefix}
                              </Link>
                            </MDBox>
                          </MDBox>
                        </TableCell>
                        <TableCell>{getProposalCompanyLabel(proposal)}</TableCell>
                        <TableCell>{renderStageNames(proposal.stages)}</TableCell>
                        <TableCell>{getProposalStatusLabel(proposal)}</TableCell>
                        <TableCell>{proposal?.user?.name ?? ""}</TableCell>
                        <TableCell>
                          {proposal.contact?.firstName + " " + proposal.contact?.lastName}
                        </TableCell>
                        <TableCell>
                          {proposal.createdAt
                            ? dayjs(proposal.createdAt).format("YYYY-MM-DD")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {proposal.sentAt ? dayjs(proposal.sentAt).format("YYYY-MM-DD") : "-"}
                        </TableCell>
                        <TableCell align="right">
                          {formatCentsToUSD(proposal.overallTotal)}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const lastActivity = getLastActivity(proposal);
                            if (!lastActivity) return "-";
                            const isEvent = lastActivity.type === "event";
                            const displayText = isEvent
                              ? (lastActivity.data as any)?.action?.toLowerCase().replace("_", " ")
                              : (lastActivity.data as any)?.status?.toLowerCase();
                            const displayDate = isEvent
                              ? (lastActivity.data as any)?.createdAt
                              : (lastActivity.data as any)?.changeAt;

                            return (
                              <div>
                                <div style={{ textTransform: "capitalize", fontWeight: "bold" }}>
                                  {displayText}
                                </div>
                                <div style={{ fontSize: "0.8em", color: "#666" }}>
                                  {dayjs(displayDate).format("MM/DD/YYYY")}
                                </div>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <ProposalActionsCell
                            row={{ values: proposal }}
                            deletePending={isDeletePending}
                            onDeleteStart={handleDeleteStart}
                            onDeleteSuccess={handleDeleteSuccess}
                            onDeleteSettled={handleDeleteSettled}
                          />
                        </TableCell>
                      </StyledTableRow>
                      {openStatus[proposal.id] && !isDeletePending && (
                        <ProposalExpandedRows proposal={proposal} />
                      )}
                    </React.Fragment>
                  );
                })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={10}>
                  <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {pagination.component}
                  </MDBox>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
